/**
 * @description Tenant slug helpers for unauthenticated auth API calls
 *
 * Key features:
 * - Resolves SO slug from URL param, branding, lynx.build host, or Tier-3 resolve
 * - Appends `?subdomain=` so backend reset-password / signup / forgot-password
 *   can look up the Wristband tenant when req.tenantContext is null
 *
 * @dependencies
 * - lynxBuildHostname.getLynxBuildTenantSlug: sync Tier-2 parse
 * - lynxBuildHostname.usesTenantBffOrigin / isPlatformHostname: isolation guard
 * - tenantResolveService.resolveTenantForHostname: Tier-3 custom host resolve
 *
 * @notes
 * - AcceptInvite previously used only getLynxBuildTenantSlug. Prod SO custom
 *   domains 301 off *.lynx.build, so that parse returns null and auto-login
 *   hits the default LynxPay tenant → invalid_auth_session_code.
 * - URL `subdomain` (embedded in signup/invite/reset emails) wins so the slug
 *   survives hostname changes.
 * - shouldFollowAuthRedirect never allows every *.wristband.dev. Platform and
 *   LynxPay Wristband hosts are always blocked. With a minted `wristbandTenant`,
 *   only that exact host is allowed. Without mint, only a non-LynxPay Wristband
 *   vanity (*.wristband.dev or *.auth.lynx.build) or the registered Lynx api
 *   /api/auth/router/login hop (LYN-335) is allowed.
 */

import { getLynxBuildTenantSlug, isPlatformHostname, usesTenantBffOrigin } from './lynxBuildHostname';
import { resolveTenantForHostname } from '../services/api/tenantResolveService';

/**
 * Append `subdomain` to an API path that may already have a query string.
 *
 * @param {string} path
 * @param {string|null|undefined} subdomain
 * @returns {string}
 */
export function withAuthSubdomain(path, subdomain) {
  if (!subdomain) {
    return path;
  }
  const join = path.includes('?') ? '&' : '?';
  return `${path}${join}subdomain=${encodeURIComponent(subdomain)}`;
}

/**
 * Resolve SO slug for auth POSTs (signup, reset-password, forgot-password).
 *
 * Precedence: URL `subdomain` → branding slug → *.lynx.build parse → Tier-3 resolve.
 *
 * @param {object} [options]
 * @param {string|null} [options.brandingSlug]
 * @param {URLSearchParams} [options.searchParams]
 * @param {string} [options.hostname]
 * @returns {Promise<string|null>}
 */
export async function resolveAuthSubdomain({
  brandingSlug = null,
  searchParams = null,
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
} = {}) {
  const fromUrl = typeof searchParams?.get === 'function'
    ? searchParams.get('subdomain')
    : null;
  if (fromUrl && fromUrl.trim()) {
    return fromUrl.trim();
  }

  if (brandingSlug) {
    return brandingSlug;
  }

  const lynxSlug = getLynxBuildTenantSlug(hostname);
  if (lynxSlug) {
    return lynxSlug;
  }

  const resolved = await resolveTenantForHostname(hostname);
  return resolved?.slug || null;
}

/**
 * LynxPay Wristband vanities that must never be followed as auto-login hops.
 *
 * @param {string} hostname
 * @returns {boolean}
 */
function isLynxPayWristbandHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  if (host === 'lynxpay.auth.lynx.build') {
    return true;
  }
  return host.startsWith('lynxpay.')
    && (host.endsWith('.wristband.dev') || host.endsWith('.auth.lynx.build'));
}

/**
 * Platform LynxPay SPA hosts plus LynxPay Wristband vanities.
 *
 * @param {string} hostname
 * @returns {boolean}
 */
function isLynxPayOrPlatformHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host === 'app.lynxpay.us'
    || isPlatformHostname(host)
    || isLynxPayWristbandHost(host);
}

/**
 * Non-LynxPay Wristband vanity (create-auth-session host).
 *
 * @param {string} hostname
 * @returns {boolean}
 */
function isWristbandVanityHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host.endsWith('.wristband.dev') || host.endsWith('.auth.lynx.build');
}

/**
 * Registered Lynx Application Login URI. Wristband already lists api. /
 * existing login+callback — never admin.{cp} / app.{cp}.
 *
 * @param {string} redirectUrl
 * @returns {boolean}
 */
function isRegisteredLynxApiLoginHop(redirectUrl) {
  try {
    const parsed = new URL(redirectUrl);
    const host = parsed.hostname.toLowerCase();
    if (parsed.pathname !== '/api/auth/router/login') {
      return false;
    }
    return host === 'api.lynx.build' || host === 'api.staging.lynx.build';
  } catch {
    return false;
  }
}

/**
 * Whether the SPA should navigate to an auto-login redirectUrl.
 *
 * Always blocks app.lynxpay.us, LynxPay Wristband vanities, and platform hosts.
 * With a minted vanity, only that exact host is allowed. Without mint, only a
 * non-LynxPay Wristband vanity or the registered api /router/login hop is
 * allowed — never an arbitrary https host.
 *
 * @param {string|null|undefined} redirectUrl
 * @param {string} [hostname]
 * @param {string|null} [mintedWristbandTenant] - `wristbandTenant` from the email URL
 * @returns {boolean}
 */
export function shouldFollowAuthRedirect(
  redirectUrl,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  mintedWristbandTenant = null
) {
  if (!redirectUrl || typeof redirectUrl !== 'string') {
    return false;
  }

  if (!/^https?:\/\//i.test(redirectUrl)) {
    return false;
  }

  let targetHost;
  try {
    targetHost = new URL(redirectUrl, `https://${hostname || 'localhost'}`).hostname;
  } catch {
    return false;
  }

  const target = targetHost.toLowerCase();

  if (isRegisteredLynxApiLoginHop(redirectUrl)) {
    return true;
  }

  if (isLynxPayOrPlatformHost(target)) {
    return false;
  }

  const minted = typeof mintedWristbandTenant === 'string'
    ? mintedWristbandTenant.trim()
    : '';

  if (minted) {
    if (isLynxPayOrPlatformHost(minted)) {
      return false;
    }
    return target === minted.toLowerCase();
  }

  if (isWristbandVanityHost(target) && !isLynxPayWristbandHost(target)) {
    return true;
  }

  if (usesTenantBffOrigin(hostname) && isPlatformHostname(targetHost)) {
    return false;
  }

  return false;
}
